import type { Directive } from 'vue';

const enum KEYS {
    Enter = 'Enter',
    Space = ' ',
    Tab = 'Tab'
}

const CONTAINER_ATTR = 'focus-container';
const ACTIVE_ATTR = 'focus-container-active';
const LIST_ATTR = 'focus-list';
const ICON_ATTR = 'focus-icon';
const FOCUS_ATTRS = `[${LIST_ATTR}],[${CONTAINER_ATTR}]`;
const TABBABLE_TAGS = `button,input,select,a,textarea,[contenteditable],.ag-tab-guard,[${LIST_ATTR}],[${CONTAINER_ATTR}],[${ICON_ATTR}],[tabindex]`;
const OBSERVER_OPTIONS: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['tabindex', LIST_ATTR, CONTAINER_ATTR, ICON_ATTR]
};

let managers: FocusContainerManager[] = [];

/**
 * The FocusContainer Directive
 *
 * To use; add `v-focus-container` to the element.
 * It will only allow tab access to inner elements when `enter` or `space` are pressed on the container.
 */
export const FocusContainer: Directive = {
    mounted(el: HTMLElement) {
        // check whether the element is inside a RAMP app, and proceed only if it is
        // let's hope that no one decides to use class inner-shell on their elements
        // couldn't find a better solution since we don't have access to the vue app in here
        const rampShells = [...document.querySelectorAll('.inner-shell')];
        if (rampShells.some(shell => shell.contains(el))) {
            managers.push(new FocusContainerManager(el));
        }
    },
    beforeUnmount(el: HTMLElement) {
        // filter removes the FocusContainerManager at the same time
        managers = managers.filter((manager: FocusContainerManager) => {
            if (manager.element === el) {
                manager.removeEventListeners();
                return false;
            }
            return true;
        });
    }
};

/**
 * The FocusContainerManager Class
 *
 * Manages the event listeners and callbacks for `FocusContainer`s
 */
class FocusContainerManager {
    element: HTMLElement;
    isTabbingEnabled: boolean;
    initialDisableTimeout: ReturnType<typeof setTimeout> | undefined;
    mutationObserver: MutationObserver | undefined;
    readonly keypressHandler: (event: KeyboardEvent) => void;
    readonly clickHandler: () => void;
    readonly focusOutHandler: (event: FocusEvent) => void;
    readonly focusHandler: () => void;

    /**
     * Creates an instance of FocusContainerManager
     *
     * @param element The HTMLElement the directive was placed on
     * @param options The options provided to the directive
     */
    constructor(element: HTMLElement) {
        this.element = element;
        this.isTabbingEnabled = false;
        this.element.toggleAttribute(CONTAINER_ATTR, true);
        this.element.toggleAttribute(ACTIVE_ATTR, false);
        this.element.tabIndex = 0;
        this.initialDisableTimeout = setTimeout(() => this.disableTabbing(), 600); // elements of container need more time to render, otherwise they wont be detected
        this.keypressHandler = (event: KeyboardEvent) => {
            this.onKeypress(event);
        };
        this.clickHandler = () => {
            this.onClick();
        };
        this.focusOutHandler = (event: FocusEvent) => {
            this.onFocusOut(event);
        };
        this.focusHandler = () => {
            this.onFocus();
        };

        this.element.addEventListener('keypress', this.keypressHandler);
        this.element.addEventListener('click', this.clickHandler);
        this.element.addEventListener('focusout', this.focusOutHandler);
        this.element.addEventListener('focus', this.focusHandler);

        // Keep late-mounted or externally-retabbed descendants out of the tab order
        // until the user explicitly activates the container.
        this.mutationObserver = new MutationObserver(() => {
            if (!this.isTabbingEnabled) {
                this.disableTabbing();
            }
        });
        this.observeMutations();
    }

    /**
     * Removes all of the event listeners on the container element.
     */
    removeEventListeners() {
        if (this.initialDisableTimeout) {
            clearTimeout(this.initialDisableTimeout);
        }
        this.setTabbingEnabled(false);
        this.mutationObserver?.disconnect();
        this.element.removeEventListener('keypress', this.keypressHandler);
        this.element.removeEventListener('click', this.clickHandler);
        this.element.removeEventListener('focusout', this.focusOutHandler);
        this.element.removeEventListener('focus', this.focusHandler);
    }

    /**
     * Callback for the `keypress` event on the container element
     *
     * @param event The keyboard event
     */
    onKeypress(event: KeyboardEvent) {
        if (event.target !== this.element) {
            return;
        }
        if (event.key === KEYS.Enter || event.key === KEYS.Space) {
            this.enableTabbing()?.focus();
        }
    }

    /**
     * Callback for the `click` event on the container element
     */
    onClick() {
        this.enableTabbing();
    }

    /**
     * Callback for the `focusout` event on the container element
     *
     * @param event The focus event
     */
    onFocusOut(event: FocusEvent) {
        if (!this.element.contains(event.relatedTarget as HTMLElement)) {
            this.disableTabbing();
        }
    }

    /**
     * Callback for the `focus` event on the container element
     */
    onFocus() {
        this.disableTabbing();
    }

    /**
     * Sets tabindex to -1 for EVERY element under the container element
     */
    disableTabbing() {
        // Keep current descendant focus intact while the user is actively traversing the container.
        // The container itself is the locked state, so it should still trigger a rescan.
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && activeElement !== this.element && this.element.contains(activeElement)) {
            return;
        }
        this.setTabbingEnabled(false);
        const tab_list = Array.prototype.filter.call(this.element.querySelectorAll(TABBABLE_TAGS), () => {
            return true;
        }) as HTMLElement[];

        this.withPausedObserver(() => {
            tab_list.forEach((el: HTMLElement) => {
                if (el.tabIndex !== -1) {
                    el.tabIndex = -1;
                }
            });
        });
    }

    /**
     * Sets tabindex to 0 for every VISIBLE element not under a different focus container or list.
     * @return {HTMLElement} the first valid element
     */
    enableTabbing() {
        this.setTabbingEnabled(true);
        let first_tabbable_item: any = undefined;
        this.withPausedObserver(() => {
            Array.prototype.map.call(this.element.querySelectorAll(TABBABLE_TAGS), el => {
                // !!el.offsetParent means it is visible
                if (
                    (el.closest(FOCUS_ATTRS) === this.element ||
                        (el.closest(FOCUS_ATTRS) === el &&
                            el.parentElement!.closest(FOCUS_ATTRS) === this.element)) &&
                    !!el.offsetParent
                ) {
                    if (el.tabIndex !== 0) {
                        el.tabIndex = 0;
                    }
                    if (first_tabbable_item === undefined) {
                        first_tabbable_item = el;
                    }
                }
            });
        });
        return first_tabbable_item;
    }

    private setTabbingEnabled(enabled: boolean) {
        this.isTabbingEnabled = enabled;
        this.element.toggleAttribute(ACTIVE_ATTR, enabled);
    }

    private observeMutations() {
        this.mutationObserver?.observe(this.element, OBSERVER_OPTIONS);
    }

    private withPausedObserver(callback: () => void) {
        this.mutationObserver?.disconnect();
        try {
            callback();
        } finally {
            this.observeMutations();
        }
    }
}
